"use client";

import { useEffect, useMemo, useState } from "react";
import type { Session } from "next-auth";

import type { Service } from "@/types";
import { errorIntoString } from "@/lib/utils";
import { formatServiceDisplayTitle } from "@/lib/utils/service-title";
import {
	isServiceLivePreviewAvailable,
	normalizeServiceDemoUrl,
} from "@/lib/utils/service-live-preview";
import { getProxiedImageUrl } from "@/lib/utils/image-proxy";
import {
	useServiceCountdownOfferOverlay,
	type ServiceCountdownOfferOverlay,
} from "@/hooks/useServiceCountdownOfferOverlay";
import {
	useGetSavedServicesQuery,
	useSetSavedServicesMutation,
} from "@/features/saved/savedApiInjections";
import { ga4SaveItem, ga4LivePreviewOpen, ga4SelectPromotion, ga4CtaClick } from "@/lib/analytics/ga4-ecommerce";

import { useToast } from "@/components/ui/use-toast";
import MediaCarousel, { type LiveDemoFullscreenConfig } from "@/components/carousels/MediaCarousel";
import { dedupeServiceMediaForCarousel } from "@/lib/utils/service-media-image";
import SignupPromptModal from "@/components/auth/SignupPromptModal";
import type { CatalogSuggestion } from "@/types";

type Props = {
	session: Session | null;
	service: Service;
	/** Opens full-window live demo (iframe + basket actions) from the enlarge control. */
	liveDemoFullscreen?: LiveDemoFullscreenConfig;
	/** Changes when the shopper picks another design, so Live Preview returns to Screenshots. */
	designSelectionKey?: string | null;
};

export default function ServiceMediaCarousel({
	session,
	service,
	liveDemoFullscreen,
	designSelectionKey = null,
}: Props) {
	const { toast } = useToast();
	const [setFav, { isLoading: isFavoriteSetting }] = useSetSavedServicesMutation();
	const userId = useMemo(() => {
		const raw = session?.user?.id;
		if (raw == null) return null;
		const s = String(raw).trim();
		return s.length > 0 ? s : null;
	}, [session?.user?.id]);
	const hasValidSession = Boolean(session?.idToken && session.idToken.trim().length > 0);
	const { data: saved, isLoading: isFavoriteLoading } = useGetSavedServicesQuery(
		userId ?? "",
		{
			skip: userId == null || !hasValidSession,
			refetchOnMountOrArgChange: false,
			refetchOnFocus: false,
			refetchOnReconnect: false,
		},
	);

	const isFavorite = useMemo(
		() =>
			saved === undefined
				? false
				: saved.serviceIds.findIndex((x) => x._id === service._id) !== -1,
		[saved, service._id],
	);

	const offerOverlay: ServiceCountdownOfferOverlay | null = useServiceCountdownOfferOverlay(service);

	const [previewMode, setPreviewMode] = useState<"images" | "live-demo">("images");
	const [showSignupModal, setShowSignupModal] = useState(false);

	useEffect(() => {
		setPreviewMode("images");
	}, [designSelectionKey]);

	useEffect(() => {
		if (service.livePreviewDisabled === true && previewMode === "live-demo") {
			setPreviewMode("images");
		}
	}, [service.livePreviewDisabled, previewMode]);

	const displayTitle = formatServiceDisplayTitle(service.title);
	const galleryMedia = useMemo(
		() => dedupeServiceMediaForCarousel(service.media ?? []),
		[service.media],
	);
	const serviceSharePath = `/services/${service.slug || service._id}`;
	const serviceReturnUrl = process.env.NEXT_PUBLIC_FRONTEND_URI
		? `${process.env.NEXT_PUBLIC_FRONTEND_URI}${serviceSharePath}`
		: serviceSharePath;

	const demoEmbedSrc = normalizeServiceDemoUrl(service.demoUrl);
	const livePreviewAvailable = isServiceLivePreviewAvailable(service);

	/** Warm TLS/DNS for the demo host (cannot cache third-party iframe HTML ourselves without a proxy). */
	useEffect(() => {
		if (!demoEmbedSrc || !livePreviewAvailable) return;
		let origin: string;
		try {
			origin = new URL(demoEmbedSrc).origin;
		} catch {
			return;
		}
		const links: HTMLLinkElement[] = [];
		const append = (rel: string, href: string, crossOrigin?: "anonymous") => {
			const link = document.createElement("link");
			link.rel = rel;
			link.href = href;
			if (crossOrigin) link.crossOrigin = crossOrigin;
			document.head.appendChild(link);
			links.push(link);
		};
		append("dns-prefetch", origin);
		append("preconnect", origin, "anonymous");
		return () => {
			for (const link of links) {
				link.remove();
			}
		};
	}, [demoEmbedSrc, livePreviewAvailable]);

	const handleFavoriteClick = async () => {
		if (!session || !hasValidSession) {
			setShowSignupModal(true);
			return;
		}

		if (saved === undefined) return;

		try {
			if (isFavorite) {
				await setFav({
					userId: session.user.id,
					serviceIds: [
						...saved?.serviceIds.map((id) => id._id).filter((id) => id !== service.id),
					],
				});

				ga4SaveItem({
					content_type: "service",
					item_id: service._id,
					item_name: displayTitle,
					saved: false,
				});
				return toast({ title: `${displayTitle} removed from saved` });
			}

			await setFav({
				userId: session.user.id,
				serviceIds: [...saved?.serviceIds.map((id) => id._id), service._id],
			});

			ga4SaveItem({
				content_type: "service",
				item_id: service._id,
				item_name: displayTitle,
				saved: true,
			});
			toast({ title: `${displayTitle} saved` });
		} catch (error: any) {
			toast({
				title: "Error error saving service",
				description: errorIntoString(error),
				variant: "destructive",
			});
		}
	};

	const showPreviewToggle = livePreviewAvailable && demoEmbedSrc != null;
	const firstServiceImage = galleryMedia.find((item) => {
		if (!item || item.type?.toUpperCase() === "VIDEO") return false;
		return Boolean(item.displayUrl?.trim() || item.url?.trim());
	});
	const rawShareImageUrl =
		firstServiceImage?.displayUrl?.trim() ||
		firstServiceImage?.url?.trim() ||
		service.thumbnailImage?.trim() ||
		"";
	const shareCatalogSuggestion: CatalogSuggestion = {
		type: "service",
		title: displayTitle,
		label: service.categoryId?.title || service.eyebrowText || "Service",
		description: (service.excerpt || service.description || "")
			.replace(/<[^>]*>/g, " ")
			.replace(/\s+/g, " ")
			.trim()
			.slice(0, 220),
		url: serviceSharePath,
		imageUrl: getProxiedImageUrl(rawShareImageUrl) ?? rawShareImageUrl,
		meta: service.subcategoryIds
			?.map((category) => category?.title)
			.filter((tag): tag is string => Boolean(tag?.trim()))
			.slice(0, 2)
			.join(" · "),
		tags:
			service.subcategoryIds
				?.map((category) => category?.title)
				.filter((tag): tag is string => Boolean(tag?.trim()))
				.slice(0, 5) ?? [],
		service,
	};

	return (
		<>
			<div className="w-full">
				<div
					role="tabpanel"
					id="service-preview-panel"
					aria-labelledby={
						previewMode === "images" ? "service-preview-tab-images" : "service-preview-tab-live-demo"
					}
				>
					<MediaCarousel
						key={designSelectionKey ?? "landing"}
						media={galleryMedia}
						priorityFirstImage={true}
						preferOriginalImages
						isFavorite={isFavorite}
						isFavoriteLoading={isFavoriteLoading || isFavoriteSetting}
						onFavoriteClick={handleFavoriteClick}
						shareLink={serviceReturnUrl}
						shareCatalogSuggestion={shareCatalogSuggestion}
						offerOverlay={offerOverlay}
						onOfferOverlayClick={
							offerOverlay
								? () => {
										ga4SelectPromotion({
											promotion_id: service._id,
											promotion_name: offerOverlay.label,
											creative_name: "service_media_offer_pill",
										});
										document
											.getElementById("service-package-card")
											?.scrollIntoView({ behavior: "smooth", block: "start" });
									}
								: undefined
						}
						liveDemoUrl={showPreviewToggle ? demoEmbedSrc : null}
						previewMode={showPreviewToggle ? previewMode : undefined}
						onPreviewModeChange={
							showPreviewToggle
								? (mode) => {
										if (mode === previewMode) return;
										ga4CtaClick({
											cta_id:
												mode === "live-demo"
													? "service_media_live_preview"
													: "service_media_screenshots",
											cta_name: mode === "live-demo" ? "Live Preview" : "Screenshots",
											cta_location: "service_media_container",
										});
										setPreviewMode(mode);
										if (mode === "live-demo") {
											ga4LivePreviewOpen({
												item_id: service._id,
												item_name: displayTitle,
											});
										}
									}
								: undefined
						}
						demoEmbedSrc={previewMode === "live-demo" && showPreviewToggle ? demoEmbedSrc : null}
						demoExclusive={previewMode === "live-demo"}
						elevated
						liveDemoFullscreen={livePreviewAvailable ? liveDemoFullscreen : undefined}
						keepSideArrowsVisible
					/>
				</div>
			</div>
			<SignupPromptModal
				open={showSignupModal}
				onOpenChange={setShowSignupModal}
				returnUrl={serviceReturnUrl}
				title="Sign up to save this service"
				description="Create a Techxeed account before saving this service."
			/>
		</>
	);
}
